// frontend/src/utils/username.js
// Unified username helpers
// — Portal rule: first3(first) + first3(last) + NNN (001..999), letters only
// — Fallbacks: infer last from email if missing; ensure uniqueness vs a Set

const stripAccents = (s) =>
  (s || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "");

const onlyLetters = (s) => stripAccents(String(s || "")).toLowerCase().replace(/[^a-z]/g, "");
const pad3 = (s) => (s + "xxx").slice(0, 3);
const z3 = (n) => String(n).padStart(3, "0");

const guessSurnameFromEmail = (email) => {
  const local = String(email || "").split("@")[0];
  if (!local) return "";
  const parts = local.split(/[._-]+/).filter(Boolean);
  // pick the longest token that isn't a single letter
  const best = parts
    .filter((p) => p && p.length > 1)
    .sort((a, b) => b.length - a.length)[0] || parts[0] || "";
  return best.replace(/[^a-z]/gi, "");
};

export function buildPortalBase(firstName, lastName, email) {
  const f3 = pad3(onlyLetters(firstName));
  let lRaw = onlyLetters(lastName);
  if (!lRaw) lRaw = onlyLetters(guessSurnameFromEmail(email));
  const l3 = pad3(lRaw);
  return `${f3}${l3}`; // always 6 chars (letters only, padded with x if short)
}

export function ensurePortalUnique(base6, existingSet) {
  const set = existingSet || new Set();
  for (let i = 1; i <= 999; i++) {
    const candidate = `${base6}${z3(i)}`.toLowerCase();
    if (!set.has(candidate)) return candidate;
  }
  // extreme fallback
  const rnd = Math.floor(Math.random() * 900 + 100);
  return `${base6}${rnd}`.toLowerCase();
}

/**
 * makePortalUsername(first, last, email, existingSet)
 * Returns e.g. 'chrdun001' for Chris Dunstan (if available)
 */
export function makePortalUsername(first, last, email, existingSet) {
  const base6 = buildPortalBase(first, last, email);
  return ensurePortalUnique(base6, existingSet);
}

/* -------------------------------------------------------------------------
 * (Keeping your previous general helpers in case other code imports them)
 * ------------------------------------------------------------------------*/
const slugPart = (s) =>
  stripAccents(String(s || ""))
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .slice(0, 30);

export function buildBaseUsername(firstName, lastName, email) {
  const f = slugPart(firstName);
  const l = slugPart(lastName);
  if (f && l) return (f[0] + l).slice(0, 30);
  if (l) return l;
  if (f) return f;
  const local = String(email || "").split("@")[0] || "user";
  return slugPart(local) || "user";
}

export function normalizeUsername(u) {
  let s = stripAccents(String(u || "").toLowerCase());
  s = s.replace(/[^a-z0-9._]+/g, "");
  if (!/^[a-z]/.test(s)) s = "u" + s;
  if (s.length < 3) s = (s + "user").slice(0, 3);
  if (s.length > 30) s = s.slice(0, 30);
  return s || "user";
}

export function ensureUniqueUsername(base, existingSet) {
  const n = normalizeUsername(base);
  const set = existingSet || new Set();
  if (!set.has(n)) return n;
  for (let i = 1; i < 10000; i++) {
    const c = normalizeUsername(`${n}${i}`);
    if (!set.has(c)) return c;
  }
  const rand = Math.floor(Math.random() * 1e9);
  return normalizeUsername(`${n}${rand}`);
}

export function makeUsername(firstName, lastName, email, existingSet) {
  const base = buildBaseUsername(firstName, lastName, email);
  return ensureUniqueUsername(base, existingSet);
}
